import type { ProjectTimeEntry } from '../../types';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfLocalDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function entryWorkDate(e: ProjectTimeEntry): Date {
  return e.startTime ?? e.date;
}

/** Stable key for Recharts dataKey (Firestore ids can contain chars Recharts dislikes). */
export function userSeriesKey(userId: string): string {
  return `u_${userId.replace(/[^a-zA-Z0-9]/g, '_')}`;
}

export interface ProjectTimeChartSeries {
  key: string;
  userId: string;
  displayName: string;
  color: string;
}

const SERIES_COLORS = ['#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#fb923c', '#2dd4bf', '#e879f9'];

export interface BuildProjectTimeChartResult {
  chartData: Record<string, string | number>[];
  series: ProjectTimeChartSeries[];
  empty: boolean;
}

/** Invoice (or other) minutes already resolved to a person and a calendar day. */
export interface ProjectTimeChartExtraMinutes {
  userId: string;
  userName: string;
  minutes: number;
  date: Date;
}

/**
 * Bucket minutes by calendar day (local) and user. Fills in zero days between first and last activity.
 * `extraMinutes` are added on their own date so hover totals include designer-invoice hours.
 */
export function buildProjectTimeDailyChartData(
  entries: ProjectTimeEntry[],
  extraMinutes: ProjectTimeChartExtraMinutes[] = [],
): BuildProjectTimeChartResult {
  const extras = extraMinutes.filter(row => row.minutes > 0 && row.date instanceof Date && !Number.isNaN(row.date.getTime()));
  if (!entries.length && !extras.length) {
    return { chartData: [], series: [], empty: true };
  }

  const userMeta = new Map<string, string>();
  entries.forEach(e => {
    if (!userMeta.has(e.userId)) userMeta.set(e.userId, e.userName || 'User');
  });
  extras.forEach(row => {
    if (!userMeta.has(row.userId)) userMeta.set(row.userId, row.userName || 'User');
  });

  const userIds = Array.from(userMeta.keys()).sort();

  let minDay = Infinity;
  let maxDay = -Infinity;
  const noteDay = (t: number) => {
    minDay = Math.min(minDay, t);
    maxDay = Math.max(maxDay, t);
  };
  entries.forEach(e => noteDay(startOfLocalDay(entryWorkDate(e))));
  extras.forEach(row => noteDay(startOfLocalDay(row.date)));

  const days: number[] = [];
  for (let t = minDay; t <= maxDay; t += DAY_MS) {
    days.push(t);
  }

  const minutesByDayUser = new Map<number, Map<string, number>>();
  days.forEach(d => {
    const m = new Map<string, number>();
    userIds.forEach(uid => m.set(uid, 0));
    minutesByDayUser.set(d, m);
  });

  const addMinutes = (day: number, userId: string, minutes: number) => {
    const bucket = minutesByDayUser.get(day);
    if (!bucket) return;
    bucket.set(userId, (bucket.get(userId) || 0) + minutes);
  };
  entries.forEach(e => addMinutes(startOfLocalDay(entryWorkDate(e)), e.userId, e.minutes));
  extras.forEach(row => addMinutes(startOfLocalDay(row.date), row.userId, row.minutes));

  const series: ProjectTimeChartSeries[] = userIds.map((uid, i) => ({
    key: userSeriesKey(uid),
    userId: uid,
    displayName: userMeta.get(uid) || 'User',
    color: SERIES_COLORS[i % SERIES_COLORS.length],
  }));

  const useCompactDates = days.length > 12;

  const chartData = days.map(t => {
    const row: Record<string, string | number> = {
      dateKey: t,
      dateLabel: formatChartDayLabel(t, useCompactDates),
    };
    const bucket = minutesByDayUser.get(t)!;
    userIds.forEach(uid => {
      row[userSeriesKey(uid)] = Math.round((bucket.get(uid) || 0) * 10) / 10;
    });
    return row;
  });

  return { chartData, series, empty: false };
}

function formatChartDayLabel(ts: number, compact: boolean): string {
  const d = new Date(ts);
  if (compact) {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
